import { get, set } from 'lodash';

import AppDispatcher from '../../dispatcher/AppDispatcher';
import { EventEmitter } from 'events';
import AccountsApiUtils from '../../utils/AccountsApiUtils';
import * as FavouritesApiUtils from '../../utils/FavouritesApiUtils';
import FavouritesConstants from '../../constants/FavouritesConstants';
import AppConstants, { CHANGE_EVENT } from '../../constants/AppConstants';
import envConfig from '../../../static/config.js';
import { LARGE_CUSTOMER_MIN_ACCOUNTS_SIZE } from '../../constants/LargeCustomerViewConstants';
import AccountParser from '../../parsers/AccountParser';
import * as FavouritesStoreUtils from '../../utils/FavouritesStoreUtils';
import * as PermissionsUtils from '../../utils/PermissionsUtils';
import * as FavouritesUtils from '../../utils/FavouritesUtils';
import { isSpendingsEnabled } from '../../utils/SpendingsUtils';
import { isEnrichCapabilityAvailable } from '../../utils/AccountUtils';
import { youthAccountProductCodes } from '../../constants/YouthAccountConstants';

class FavouritesStore extends EventEmitter {
  constructor() {
    super();
    this.dispatchToken = AppDispatcher.register(
      this.dispatcherCallback.bind(this)
    );
    this.state = FavouritesStoreUtils.setInitialState();
  }

  emitChange = () => {
    this.emit(CHANGE_EVENT);
  };

  addChangeListener = (callback) => {
    this.on(CHANGE_EVENT, callback);
  };

  removeChangeListener = (callback) => {
    this.removeListener(CHANGE_EVENT, callback);
  };

  getAll = () => this.state.accountList || [];

  getFavouritesAndSelectedAccount = () => {
    const { accountList = [], selectedAccount } = this.state;
    return accountList.find((a) => a.id === selectedAccount.id)
      ? accountList
      : accountList.concat(selectedAccount);
  };

  getAccountDetail = (id) =>
    this.getFavouritesAndSelectedAccount().find((a) => a.id === id) || {};

  getAccountType = (id) =>
    this.state.accountTypeColor.find((colour) => colour.accntId === id);

  getAccountTypeFromAccountId = (id) => {
    const account =
      this.getFavouritesAndSelectedAccount().find((a) => a.id === id) || {};
    return account.type;
  };

  setAccountDetailsBalance = () => {
    this.state = {
      ...this.state,
      accountsLoaded: true,
    };

    this.emitChange();
  };

  setAccountID = (id) =>
    (this.state.selectedAccount =
      this.state.accountList.find((a) => a.id === id) || {});

  getAccount = () => this.state.selectedAccount.id;

  getAccountByAccountNumberSortCode = (accountNumber, sortCode) =>
    FavouritesStoreUtils.getAccountByAccountNumberSortCode({
      accountList: this.getFavouritesAndSelectedAccount(),
      accountNumber,
      sortCode,
    });

  getAccountIdList = () => this.state.accountList.map((acc) => acc.id);

  getAllCapableAccounts = (capability) =>
    PermissionsUtils.getAccountsWithCapability({
      accountList: this.state.accountList,
      capability,
    });

  isManageDebitCardEnabled = () =>
    PermissionsUtils.isManageDebitCardEnabled(this.state.accountList);

  getAllSMEPaymentCapableAccounts = () =>
    PermissionsUtils.getAllSMEPaymentCapableAccounts(this.state.accountList);

  SMEHasPaymentCapability = () =>
    this.state.accountList.length === 0 ||
    this.getAllSMEPaymentCapableAccounts().length > 0;

  getALlPaymentCapableAccounts = () =>
    PermissionsUtils.getPaymentCapableAccounts(this.state.accountList);

  getAllAccountsForPaymentsHistory = () =>
    PermissionsUtils.getAllAccountsForPaymentsHistory(this.state.accountList);

  getDefaultAccountForBeneficiaryCreation = () =>
    PermissionsUtils.getDefaultAccountForBeneficiaryCreation(
      this.state.accountList
    );

  reSortAccounts = () => {
    this.state.accountList = FavouritesUtils.sortByDisplayOrder(
      this.state.accountList
    );
  };
  shouldShowGiveNoticeQuickLink = () => false;
  getLoad = () => this.state.favouritesLoaded;
  getError = () => this.state.error;
  getStatus = () => this.state.status;
  getErrorFlag = () => this.state.showError;
  getAccounts = () => this.state.accountList;
  getAlertsFlag = () => this.state.permissions.alertsEnabled;
  getSweepsFlag = () => this.state.permissions.sweepsEnabled;
  getSweepsOutFlag = () => this.state.permissions.sweepsOutEnabled;
  getStmtPreferenceFlag = () =>
    this.state.permissions[PermissionsUtils.stmtPreferenceCapability] === true;
  getPotsFlag = () => this.state.permissions.potsEnabled;
  getSpendingsFlag = () => this.state.permissions.spendingsEnabled;
  getManagePaymentsFlag = () =>
    Boolean(
      PermissionsUtils.managePaymentPermissions.filter(
        (perm) => this.state.permissions[perm] === true
      ).length
    );
  getManagePayeesFlag = () =>
    this.state.accountList.length === 0 ||
    this.state.permissions.managePayeesEnabled;
  isCreditCardUser = () =>
    this.state.accountList.length > 1 &&
    this.state.permissions.isCreditCardUser;
  getAccountsLoadedFlag = () => this.state.accountsLoaded;
  getAccountDetailsLoadedFlag = () =>
    get(this.state, 'selectedAccount.accountDetails.isLoaded', false);
  getAccountDetailsInError = () =>
    !!get(this.state, 'selectedAccount.accountDetails.error', false);

  getNavigationReference = () => this.state.navigationReference;
  getPaymentsFlag = () =>
    this.state.permissions[PermissionsUtils.paymentCapability] === true;
  isSingleAccount = () => this.state.accountList.length === 1;
  isLargeCustomer = () =>
    this.state.totalNumberOfAccounts >= LARGE_CUSTOMER_MIN_ACCOUNTS_SIZE;
  getTotalNumberOfAccounts = () => this.state.totalNumberOfAccounts;
  getAccountData = () => this.state.accountList;
  getAccountPickerBackUrl = () => this.state.accountPickerActions.backUrl;
  getAccountPickerOnSelect = () =>
    this.state.accountPickerActions.onSelectAccount;
  isManageCardEnabled = () => this.state.manageCardEnabled;
  hasAccountWithViewBalanceCapabilityAsFalse = () =>
    PermissionsUtils.getAccountsWithCapabilityAsFalse(
      this.state.accountList,
      PermissionsUtils.viewBalanceCapability
    ).length > 0 || false;
  getAlertsAvailableSingleAccount = () =>
    PermissionsUtils.getIdOfSingleAlertsAccount(this.state.accountList);

  getUIState = () => ({
    loading: this.state.loading,
    updating: this.state.updating,
    error: this.state.error,
  });

  clearAccountDetailsIsLoaded = () => {
    set(this.state, 'selectedAccount.accountDetails.isLoaded', false);
  };

  isEnriched = () => {
    /**
     * Quite confusing - getAccount() method returns the actual ID
     * rather than details object.
     */
    const accountId = this.getAccount();

    if (!accountId) {
      return false;
    }

    return isEnrichCapabilityAvailable(this.getAccountDetail(accountId));
  };

  getAccountReorderErrorFlag = () => this.state.accountReorderError;
  isYouthAccount() {
    if (this.state.accountList.length > 0) {
      const { product, role } = this.state.accountList[0];
      return (
        role === 'OWNER' && youthAccountProductCodes.includes(product.code)
      );
    }
  }
  resetStore = () => FavouritesStoreUtils.setInitialState();

  dispatcherCallback(data) {
    const actionType = data.action.actionType;
    const actionData = data.action;

    switch (actionType) {
      case FavouritesConstants.REQUEST_QUICKLINK_PERMISSIONS:
        FavouritesStoreUtils.fetchQuickLinkPermissions();
        this.emitChange();
        break;
      case FavouritesConstants.REQUEST_QUICKLINK_PERMISSIONS_SUCCESS:
        this.state = {
          ...this.state,
          permissions: {
            ...this.state.permissions,
            [get(actionData, 'data.page_meta_data.payment_type')]: Boolean(
              get(actionData, 'data.total_number_of_accounts')
            ),
          },
        };
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_FAVOURITE_ACCOUNTS:
        this.state = {
          ...this.state,
          loading: true,
          showError: false,
        };
        FavouritesApiUtils.getFavouriteAccounts();
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_FAVOURITE_ACCOUNTS_SUCCESS:
        const { accountList, totalNumberOfAccounts } = actionData.data;
        this.state = FavouritesStoreUtils.populateFavourites({
          state: this.state,
          accountList,
          totalNumberOfAccounts,
          isSpendingsEnabled: isSpendingsEnabled(),
        });

        FavouritesStoreUtils.makePotsCallsForAccountList({
          accountList,
          envConfig,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_ACCOUNT_DETAILS:
        this.state = {
          ...this.state,
          accountsLoaded: false,
          selectedAccount: {
            ...this.state.selectedAccount,
            accountDetails: {
              ...this.state.selectedAccount.accountDetails,
              isLoaded: false,
            },
          },
        };
        AccountsApiUtils.getAccountDetails(actionData.data);
        break;

      case FavouritesConstants.REQUEST_ACCOUNT_DETAILS_SUCCESS:
        this.state = FavouritesStoreUtils.handleDetailsSuccess({
          accountDetails: {
            ...actionData.data,
            isLoaded: true,
          },
          state: this.state,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_ACCOUNT_DETAILS_ERROR:
        this.state = FavouritesStoreUtils.handleDetailsError({
          accountDetails: actionData.data,
          state: this.state,
          id: actionData.id,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_FAVOURITE_ACCOUNTS_FAILURE:
        this.state = {
          ...this.state,
          loading: false,
          error: true,
        };
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_ACCEPT_TC_SUCCESS:
        this.state = {
          ...this.state,
          termsAndConditionsFlag: true,
        };
        FavouritesApiUtils.getFavouriteAccounts();
        this.emitChange();
        break;

      case FavouritesConstants.ADD_FAVOURITE_ACCOUNT:
        this.state = FavouritesStoreUtils.setUpdating(this.state);
        const parsedAccount = new AccountParser().parseFromFavouriteAccount(
          actionData.data
        );
        FavouritesStoreUtils.sendAddFavouriteRequest({
          accountList: this.state.accountList,
          account: parsedAccount,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REMOVE_FAVOURITE_ACCOUNT:
        this.state = FavouritesStoreUtils.setUpdating(this.state);
        FavouritesStoreUtils.sendRemoveFavouriteRequest({
          accountList: this.state.accountList,
          id: actionData.data,
        });
        break;

      case FavouritesConstants.ADD_FAVOURITE_ACCOUNTS_SUCCESS:
        this.state = FavouritesStoreUtils.addFavourite({
          state: this.state,
          account: actionData.data,
          isSpendingsEnabled: isSpendingsEnabled(),
        });
        this.emitChange();
        break;

      case FavouritesConstants.REMOVE_FAVOURITE_ACCOUNTS_SUCCESS:
        this.state = FavouritesStoreUtils.removeFavourite({
          state: this.state,
          id: actionData.data,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REORDER_FAVOURITE_ACCOUNTS:
        this.state = FavouritesStoreUtils.setUpdating(this.state);
        FavouritesStoreUtils.reorderFavouritesRequest(actionData.data);
        break;

      case FavouritesConstants.REORDER_FAVOURITE_ACCOUNTS_SUCCESS:
        this.state = FavouritesStoreUtils.reorderAccounts({
          state: this.state,
          reorderedIds: actionData.data,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REORDER_FAVOURITE_ACCOUNTS_FAILURE:
        this.state = {
          ...this.state,
          accountReorderError: true,
          updating: false,
        };
        this.emitChange();
        break;

      case FavouritesConstants.RENAME_ACCOUNT:
        this.state = FavouritesStoreUtils.renameAccounts({
          state: this.state,
          data: actionData.data,
          accounts: this.getFavouritesAndSelectedAccount(),
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_REJIG_POT_DETAILS_SUCCESS:
        this.state = FavouritesStoreUtils.setShowIcon({
          state: this.state,
          id: actionData.id,
          value: actionData.data.unallocated_amount.value < 0,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_RESET_REJIG_ICON:
        this.state = FavouritesStoreUtils.setShowIcon({
          state: this.state,
          id: actionData.id,
          value: actionData.data.unallocated_amount.value < 0,
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_DANGER_PERIOD_SUCCESS:
        this.state = FavouritesStoreUtils.setShowIcon({
          state: this.state,
          id: actionData.id,
          value: actionData.data.projection_periods[0].warning_days.length > 0,
        });
        this.emitChange();
        break;

      case FavouritesConstants.SET_ACCOUNT:
        this.setAccountID(actionData.id);
        break;

      case FavouritesConstants.ADD_FAVOURITE_ACCOUNTS_FAILURE:
      case FavouritesConstants.REMOVE_FAVOURITE_ACCOUNTS_FAILURE:
      case FavouritesConstants.SET_FAVOURITE_ACCOUNTS_EXCEEDED_MAX:
        this.state = {
          ...this.state,
          updating: false,
          error: true,
        };
        this.emitChange();
        break;

      case FavouritesConstants.ADD_SELECTED_ACCOUNT:
        this.state = FavouritesStoreUtils.addSelectedAccount({
          state: this.state,
          account: actionData.data,
          isSpendingsEnabled: isSpendingsEnabled(),
        });
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_TERMS_AND_CONDITIONS:
        this.state = {
          ...this.state,
          termsAndConditionsFlag: true,
        };
        this.emitChange();
        break;

      case FavouritesConstants.RESORT_ACCOUNT:
        this.state = {
          ...this.state,
          accountList: FavouritesUtils.sortByDisplayOrder(
            this.state.accountList
          ),
        };
        this.emitChange();
        break;

      case AppConstants.LOGOUT_USER:
        this.state = FavouritesStoreUtils.setInitialState();
        this.emitChange();
        break;

      case FavouritesConstants.REQUEST_ACCOUNTS_RESET_ERROR:
        this.state = {
          ...this.state,
          error: false,
        };
        this.emitChange();
        break;

      case FavouritesConstants.SET_ACCOUNT_PICKER_ACTIONS:
        this.state = {
          ...this.state,
          accountPickerActions: {
            ...actionData.data,
          },
        };
        break;

      case FavouritesConstants.CLEAR_SELECTED_ACCOUNT_ID:
        this.state = {
          ...this.state,
          selectedAccount: {},
        };
        this.emitChange();
        break;
      case FavouritesConstants.RESET_ACCOUNT_DETAILS_FLAG:
        this.state = {
          ...this.state,
          accountsLoaded: false,
        };
        break;
      default: {
        break;
      }
    }
  }
}
export default new FavouritesStore();
